import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatBytes, listDatasets, type Dataset } from '../api/datasets';
import { ApiError } from '../api/client';
import { UploadDropzone } from '../components/datasets/UploadDropzone';
import { VersionStatusBadge } from '../components/datasets/VersionStatusBadge';
import { AppShell } from '../components/layout/AppShell';
import { Card, EmptyState, ErrorState, LoadingState } from '../components/ui';
import { usePollWhile } from '../hooks/usePollWhile';
import './DatasetsPage.css';

type State = 'loading' | 'ready' | 'error';

/** `/datasets` — "Beans": everything the user has uploaded. */
export function DatasetsPage() {
  const [state, setState] = useState<State>('loading');
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    try {
      const page = await listDatasets();
      setDatasets(page.results);
      setState('ready');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setState('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // A dataset that is still being prepared would otherwise show "Brewing"
  // until the visitor reloaded the page by hand.
  const isAnyProcessing = datasets.some(
    (dataset) =>
      dataset.latest_version?.status === 'PENDING' ||
      dataset.latest_version?.status === 'PROCESSING',
  );
  usePollWhile(isAnyProcessing, load);

  return (
    <AppShell
      header={
        <div className="sc-page-header">
          <div>
            <h1>Beans</h1>
            <p>Your raw material. Upload a file and Simple Coffee prepares it for analysis.</p>
          </div>
        </div>
      }
    >
      <Card title="Add a dataset" description="CSV, XLSX or JSON.">
        <UploadDropzone onUploaded={() => void load()} />
      </Card>

      {state === 'loading' && <LoadingState label="Loading your datasets…" />}

      {state === 'error' && (
        <ErrorState
          description={error?.message ?? 'Your datasets could not be loaded.'}
          requestId={error?.requestId}
          onRetry={() => void load()}
        />
      )}

      {state === 'ready' && datasets.length === 0 && (
        <EmptyState
          title="No datasets yet"
          description="Upload your first file above. Once it is ready you will be able to explore it, shape it, and build a story around it."
        />
      )}

      {state === 'ready' && datasets.length > 0 && (
        <ul className="sc-datasets">
          {datasets.map((dataset) => (
            <li key={dataset.id}>
              <Link to={`/datasets/${dataset.id}`} className="sc-datasets__item">
                <div className="sc-datasets__main">
                  <span className="sc-datasets__name">{dataset.name}</span>
                  {dataset.description && (
                    <span className="sc-datasets__description">{dataset.description}</span>
                  )}
                  <span className="sc-datasets__meta">
                    {dataset.latest_version ? (
                      <>
                        {dataset.latest_version.row_count.toLocaleString()} rows ·{' '}
                        {dataset.latest_version.column_count} columns ·{' '}
                        {formatBytes(dataset.latest_version.source_bytes)}
                      </>
                    ) : (
                      'No versions yet'
                    )}
                  </span>
                </div>
                {dataset.latest_version && (
                  <VersionStatusBadge status={dataset.latest_version.status} />
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </AppShell>
  );
}
